
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface SelectOption {
  label: string;
  value: string;
}

interface FormSelectProps {
  name: string;
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  description?: string;
  isRequired?: boolean;
  showError?: boolean;
  fullWidth?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  validation?: Record<string, any>;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export function FormSelect({
  name,
  label,
  options,
  placeholder = "Select an option",
  description,
  isRequired = false,
  showError = true,
  fullWidth = true,
  containerClassName,
  labelClassName,
  validation = {},
  onChange,
  disabled = false,
  className,
}: FormSelectProps) {
  const form = useFormContext();
  
  // Add required validation if specified
  if (isRequired && !validation.required) {
    validation.required = "This field is required";
  }
  
  // Create an ID for accessibility
  const selectId = `form-select-${name}`;

  return (
    <div className={cn(
      'space-y-2',
      fullWidth ? 'w-full' : '',
      containerClassName
    )}>
      {form ? (
        <Controller
          name={name}
          control={form.control}
          rules={validation}
          render={({ field, fieldState: { error } }) => (
            <FormItem>
              {label && (
                <FormLabel 
                  htmlFor={selectId} 
                  className={cn(
                    isRequired ? 'after:content-["*"] after:ml-0.5 after:text-destructive' : '',
                    labelClassName
                  )}
                >
                  {label}
                </FormLabel>
              )}
              <FormControl>
                <Select
                  disabled={disabled}
                  onValueChange={(value) => {
                    field.onChange(value);
                    onChange?.(value);
                  }}
                  value={field.value}
                  defaultValue={field.value}
                >
                  <SelectTrigger 
                    id={selectId} 
                    className={cn(
                      error ? 'border-destructive focus:border-destructive' : '',
                      className
                    )}
                  >
                    <SelectValue placeholder={placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              {description && <FormDescription>{description}</FormDescription>}
              {showError && error?.message && (
                <FormMessage>{error.message}</FormMessage>
              )}
            </FormItem>
          )}
        />
      ) : (
        <div className="space-y-2">
          {label && (
            <Label 
              htmlFor={selectId}
              className={cn(
                isRequired ? 'after:content-["*"] after:ml-0.5 after:text-destructive' : '',
                labelClassName
              )}
            >
              {label}
            </Label>
          )}
          <Select disabled={disabled} onValueChange={onChange}>
            <SelectTrigger id={selectId} className={className}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      )}
    </div>
  );
}
